import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { BroadcastStateService } from '../../services/broadcaststate/BroadcastStateService';
import { LobbyService } from '../../services/lobby/LobbyService';
import { MediaService } from '../../services/media/MediaService';
import { SceneService } from '../../services/scene/SceneService';
import { StreamrAppService } from '../../services/streamrapp/StreamrAppService';
import { UserService } from '../../services/user/UserService';
import { AcceptedParticipantType, UserRole, } from '../../services/user/UserServiceOutput';
import { useTimedFlag } from '../../utils/time';
import { Button } from '../Button';
import { BroadcasterFooter } from '../broadcaster/BroadcasterFooter';
import { BroadcasterHeader } from '../broadcaster/BroadcasterHeader';
import { Canvas } from '../Canvas';
import { FilmStrip } from '../FilmStrip';
import { FullscreenButton } from '../FullscreenButton';
import { BroadcasterViewHud } from '../huds/BroadcasterViewHud';
import { ViewerViewHud } from '../huds/ViewerViewHud';
import { LoadingIndicator } from '../LoadingIndicator';
import { MetricsDisplay } from '../MetricsDisplay';
import { ScenesPanel } from '../ScenesPanel';
import { SidebarSlot } from '../SidebarSlot';
import { Stage, StageContainer } from '../Stage';
import { SplitShell } from '../shells/SplitShell';
import { StackShell } from '../shells/StackShell';
import { VideoDisplay } from '../VideoDisplay';
import { FILMSTRIP_PORTAL_ID, VideoSlotEditor } from '../VideoSlotEditor';
import { ViewerHeader } from '../viewer/ViewerHeader';
import { BlockedPermissions } from '../views/View';
import { AudioUnmuteOverlay } from './AudioUnmuteOverlay';
import { GuestFooter } from './GuestFooter';
import { Lobby } from './Lobby';
import { ParticipantButtonOverlay } from './ParticipantButtonOverlay';
import { ViewerViewFooter } from './ViewerViewFooter';
export function StreamrAppView() {
    const navigate = useNavigate();
    const activeBroadcast = BroadcastStateService.getOutput().useRequiredActiveBroadcast();
    const userRole = UserService.getOutput().useUserRole();
    const isViewerRole = userRole === UserRole.VIEWER;
    const isGuestRole = userRole === UserRole.GUEST;
    const showsViewerStream = isViewerRole || isGuestRole;
    const [showSidebar, setShowSidebar] = useState(true);
    const [activeSidebarPanel, setActiveSidebarPanel] = useState('chat');
    const [showLeftPanel, setShowLeftPanel] = useState(false);
    const streamrAppService = StreamrAppService.getNotificationInterface();
    const displayScene = SceneService.getOutput().useDisplayScene();
    const selectedLayout = displayScene?.layoutId ?? 'auto';
    const broadcastOutputBroadcasting = StreamrAppService.getOutput().useIsVideoStreaming();
    const broadcastOutputReady = StreamrAppService.getOutput().useReady();
    const sessionMode = StreamrAppService.getOutput().useSessionMode();
    const acceptedParticipantType = UserService.getOutput().useAcceptedParticipantType();
    const audioMuted = StreamrAppService.getOutput().useAudioMuted();
    const [goLivePending, setGoLivePending] = useState(false);
    const isBroadcaster = activeBroadcast.myRole === 'broadcaster';
    const lobbyView = LobbyService.getOutput().useLobbyView();
    const prevShowsViewerStreamRef = useRef(showsViewerStream);
    const prevIsGuestRoleRef = useRef(isGuestRole);
    useEffect(() => {
        if (prevShowsViewerStreamRef.current &&
            !showsViewerStream &&
            !isBroadcaster) {
            LobbyService.getInstance().notifyPromotedFromViewer();
        }
        if (!prevIsGuestRoleRef.current && isGuestRole) {
            LobbyService.getInstance().notifyPromotedFromViewer();
        }
        prevShowsViewerStreamRef.current = showsViewerStream;
        prevIsGuestRoleRef.current = isGuestRole;
    }, [showsViewerStream, isGuestRole, isBroadcaster]);
    const stageContainerRef = useRef(null);
    const [isHudVisible, showHud] = useTimedFlag(true, 5000);
    const [showMetrics, setShowMetrics] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const mediaService = MediaService.getNotificationInterface();
    const mediaOutputMicrophonePermissioned = MediaService.getOutput().useMicrophonePermissioned();
    const mediaOutputCameraPermissioned = MediaService.getOutput().useCameraPermissioned();
    const mediaOutputMediaStreamData = MediaService.getOutput().useMediaStreamData();
    const handleCanvasMount = useCallback((canvas) => {
        if (canvas) {
            streamrAppService.notifyCanvasMounted(canvas);
        }
    }, [streamrAppService]);
    const handleCanvasClick = useCallback((x, y) => {
        streamrAppService.notifyCanvasClicked(x, y);
    }, [streamrAppService]);
    // --- Speaker/host-specific effects ---
    const needsCapture = !isViewerRole;
    useEffect(() => {
        if (needsCapture) {
            mediaService.notifyStartCaptureRequested();
            return () => {
                mediaService.notifyStopCaptureRequested();
            };
        }
    }, [needsCapture, mediaService]);
    useEffect(() => {
        return () => {
            StreamrAppService.getNotificationInterface().notifyDestroyRendering();
        };
    }, []);
    useEffect(() => {
        if (needsCapture &&
            !mediaOutputMicrophonePermissioned &&
            !mediaOutputCameraPermissioned &&
            !mediaOutputMediaStreamData.isSharingScreen) {
            mediaService.notifyMediaPermissionsRequested({
                audio: true,
                video: true,
            });
        }
    }, [
        needsCapture,
        mediaOutputMicrophonePermissioned,
        mediaOutputCameraPermissioned,
        mediaOutputMediaStreamData.isSharingScreen,
        mediaService,
    ]);
    useEffect(() => {
        if (broadcastOutputBroadcasting) {
            setGoLivePending(false);
        }
    }, [broadcastOutputBroadcasting]);
    // --- Shared effects ---
    useEffect(() => {
        const permissionService = UserService.getInstance();
        permissionService.setOpenPeoplePanelCallback(() => {
            setShowSidebar(true);
            setActiveSidebarPanel('people');
        });
        return () => {
            permissionService.clearOpenPeoplePanelCallback();
        };
    }, []);
    // --- Sidebar toggle helpers ---
    const toggleSidebarPanel = (panel) => {
        setShowSidebar((current) => {
            if (!current) {
                setActiveSidebarPanel(panel);
                return true;
            }
            if (activeSidebarPanel === panel) {
                return false;
            }
            setActiveSidebarPanel(panel);
            return true;
        });
    };
    // --- Render ---
    const hasMediaPermissions = mediaOutputMicrophonePermissioned ||
        mediaOutputCameraPermissioned ||
        mediaOutputMediaStreamData.isSharingScreen;
    const showAnyLobby = lobbyView !== 'none';
    return (_jsxs(_Fragment, { children: [_jsx(LoadingIndicator, { loading: showsViewerStream
                    ? !broadcastOutputReady && !showAnyLobby
                    : goLivePending &&
                        (!broadcastOutputReady ||
                            !broadcastOutputBroadcasting) }), _jsx(StreamrAppViewRoot, { onTouchStart: () => showHud(), onMouseDown: () => showHud(), onMouseOver: () => showHud(), before: showAnyLobby ? undefined : showsViewerStream ? (_jsx(ViewerHeader, {})) : (_jsx(BroadcasterHeader, { isBroadcasting: broadcastOutputBroadcasting, showViewerCount: isBroadcaster })), after: showAnyLobby ? undefined : isGuestRole ? (_jsx(GuestFooter, { onSidebarClick: () => toggleSidebarPanel('chat'), showSidebar: showSidebar })) : showsViewerStream ? (_jsx(ViewerViewFooter, { audioMuted: audioMuted ?? false, onAudioToggle: () => streamrAppService.notifyToggleAudio(), onSidebarClick: () => toggleSidebarPanel('chat'), showSidebar: showSidebar })) : (_jsx(BroadcasterFooter, { showSidebar: showSidebar, onLeftPanelClick: () => setShowLeftPanel((c) => !c), isLeftPanelOpen: showLeftPanel, onSidebarClick: () => toggleSidebarPanel('chat'), onPeopleClick: () => toggleSidebarPanel('people'), activeSidebarPanel: activeSidebarPanel, broadcastServiceIsRunning: true, isBroadcasting: broadcastOutputBroadcasting, onGoLiveClick: () => {
                        if (displayScene) {
                            SceneService.getNotificationInterface().notifySetLiveScene(displayScene.id);
                        }
                        setGoLivePending(true);
                        streamrAppService.notifyActivateBroadcast(activeBroadcast);
                    }, onEndBroadcastClick: () => {
                        const isSessionHost = activeBroadcast.myRole === 'broadcaster';
                        const isCallSession = sessionMode === 'call' ||
                            acceptedParticipantType ===
                                AcceptedParticipantType.SPEAKERS;
                        if (isCallSession && !isSessionHost) {
                            streamrAppService.notifyDestroyRendering();
                            navigate('/');
                            setGoLivePending(false);
                            return;
                        }
                        streamrAppService.notifyDeactivateBroadcast();
                        setGoLivePending(false);
                    }, broadcastContext: "multi-broadcaster", onVideoQualityChanged: (_quality) => { } })), children: showAnyLobby ? (_jsx(Lobby, {})) : (_jsx(SplitShell, { isSidebarOpen: showSidebar, sidebar: _jsx(SidebarSlot, { activePanel: activeSidebarPanel, onClose: () => setShowSidebar(false) }), isLeftSidebarOpen: !showsViewerStream && !isGuestRole && showLeftPanel, leftSidebar: !showsViewerStream && !isGuestRole ? (_jsx(ScenesPanel, { onClose: () => setShowLeftPanel(false) })) : undefined, children: _jsxs(ContentColumn, { children: [_jsxs(FlexStageContainer, { ref: stageContainerRef, children: [_jsxs(Stage, { children: [_jsx(VideoDisplay, { fit: "contain", component: _jsx(Canvas, { onCanvasMount: handleCanvasMount, onCanvasClick: !showsViewerStream
                                                        ? handleCanvasClick
                                                        : undefined }), hud: showsViewerStream ? (_jsxs(_Fragment, { children: [_jsx(ViewerViewHud, { visible: isHudVisible, fullscreenButton: _jsx(FullscreenButton, { element: stageContainerRef.current }) }), _jsx(ParticipantButtonOverlay, { visible: isHudVisible })] })) : (_jsxs(_Fragment, { children: [_jsx(BroadcasterViewHud, { visible: isHudVisible, showIncomingRequests: isFullscreen, fullscreenButton: _jsx(FullscreenButton, { element: stageContainerRef.current, onFullscreenChange: setIsFullscreen }), onToggleMetrics: () => setShowMetrics(!showMetrics), showMetrics: showMetrics, selectedLayout: selectedLayout, onLayoutChange: (layout) => {
                                                                if (displayScene) {
                                                                    SceneService.getNotificationInterface().notifySetSceneLayoutId(displayScene.id, layout);
                                                                }
                                                            }, currentBackground: displayScene?.backgroundColor, onSelectBackground: (bg) => {
                                                                if (!displayScene)
                                                                    return;
                                                                SceneService.getNotificationInterface().notifySetSceneBackground(displayScene.id, bg);
                                                            }, onUploadBackgroundImage: (file) => {
                                                                if (!displayScene)
                                                                    return;
                                                                SceneService.getNotificationInterface().notifySetBackgroundImage(displayScene.id, file);
                                                            } }), _jsx(VideoSlotEditor, { hudVisible: isHudVisible }), _jsx(ParticipantButtonOverlay, { visible: isHudVisible })] })) }), showsViewerStream &&
                                                !broadcastOutputBroadcasting && (_jsx(ViewerPreLiveMessage, { children: "Waiting for stream..." })), !showsViewerStream &&
                                                !hasMediaPermissions && (_jsx(BlockedPermissions, { children: _jsx(Button, { type: "button", kind: "secondary", size: "big", padding: "normal", onClick: () => {
                                                        mediaService.notifyMediaPermissionsRequested({
                                                            audio: true,
                                                            video: true,
                                                        });
                                                    }, children: "Allow microphone and camera" }) }))] }), _jsx(AudioUnmuteOverlay, {}), showMetrics &&
                                        broadcastOutputBroadcasting &&
                                        !showsViewerStream && (_jsx(MetricsOverlay, { children: _jsx(MetricsDisplay, {}) }))] }), !showsViewerStream && (_jsx("div", { id: FILMSTRIP_PORTAL_ID })), isGuestRole && (_jsx(GuestFilmStripContainer, { children: _jsx(FilmStrip, {}) }))] }) })) })] }));
}
const ContentColumn = styled.div `
    display: flex;
    flex-direction: column;
    height: 100%;
`;
const FlexStageContainer = styled(StageContainer) `
    flex: 1;
    min-height: 0;
`;
const StreamrAppViewRoot = styled(StackShell) `
    --tv-ui-VideoContainer-video-border-radius: var(--tv-ui-border-radius-1);
    height: 100dvh;

    @media (min-width: 60rem) {
        --SplitShell_SidebarWidth: 25.25rem;
    }
`;
const MetricsOverlay = styled.div `
    position: absolute;
    top: 20px;
    left: 20px;
    z-index: 1000;
    pointer-events: none;

    > * {
        pointer-events: auto;
    }
`;
const GuestFilmStripContainer = styled.div `
    padding: 0.5rem 0.75rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.375rem;
`;
const ViewerPreLiveMessage = styled.div `
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: white;
    font-size: 1.5rem;
    text-align: center;
    background: rgba(0, 0, 0, 0.7);
    padding: 2rem 3rem;
    border-radius: 0.5rem;
`;
